import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { loadProjectBySlug } from '../utils/contentLoader';

const CATEGORY_META = {
  university: { label: 'University', color: '#8a9fd4' },
  work:       { label: 'Work',       color: '#7cb8a0' },
  personal:   { label: 'Personal',   color: '#c4a24a' },
};

export default function ProjectDetail() {
  const { slug } = useParams();
  const project = useMemo(() => loadProjectBySlug(slug), [slug]);

  if (!project) {
    return (
      <div style={{ padding: '48px', maxWidth: '760px' }}>
        <Link to="/projects" style={{ color: 'var(--gold)', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.7rem', textDecoration: 'none' }}>
          ← Back to Projects
        </Link>
        <h1 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', color: 'var(--text)', marginTop: '24px' }}>
          Project not found
        </h1>
        <p style={{ color: 'var(--text-3)' }}>The project "{slug}" could not be found.</p>
      </div>
    );
  }

  const meta = CATEGORY_META[project.category] || CATEGORY_META.personal;

  return (
    <div style={{ padding: '48px', maxWidth: '820px' }}>

      {/* Back link */}
      <Link
        to="/projects"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          color: 'var(--text-3)',
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.65rem',
          letterSpacing: '0.06em',
          textDecoration: 'none',
          marginBottom: '32px',
          transition: 'color 0.15s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--gold)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-3)'; }}
      >
        ← PROJECTS
      </Link>

      {/* Header */}
      <div style={{ marginBottom: '40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <span style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.6rem',
            letterSpacing: '0.08em',
            color: meta.color,
            textTransform: 'uppercase',
          }}>
            {meta.label}
          </span>
          <span style={{ color: 'var(--border-2)', fontSize: '0.7rem' }}>·</span>
          <span style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.6rem',
            color: 'var(--text-3)',
          }}>
            {project.year}
          </span>
          {project.featured && (
            <>
              <span style={{ color: 'var(--border-2)', fontSize: '0.7rem' }}>·</span>
              <span style={{ color: 'var(--gold)', fontSize: '0.75rem' }}>★ Featured</span>
            </>
          )}
        </div>

        <h1 style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontSize: 'clamp(2rem, 5vw, 3.5rem)',
          fontWeight: 700,
          letterSpacing: '-0.03em',
          lineHeight: 0.95,
          color: 'var(--text)',
          margin: '0 0 16px',
        }}>
          {project.title}
        </h1>

        {project.description && (
          <p style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontStyle: 'italic',
            fontSize: '1.2rem',
            color: 'var(--text-2)',
            margin: '0 0 20px',
            lineHeight: 1.5,
          }}>
            {project.description}
          </p>
        )}

        {/* Tags */}
        {project.tags && project.tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '8px' }}>
            {project.tags.map((tag) => (
              <span key={tag} className="tag">{tag}</span>
            ))}
          </div>
        )}
      </div>

      {/* Divider */}
      <div style={{ height: '1px', background: 'var(--border)', marginBottom: '40px' }} />

      {/* Prose content from markdown — images (including thumbnail) appear inline within the text */}
      <div
        className="prose-detail"
        dangerouslySetInnerHTML={{ __html: project.contentHtml }}
      />

      {/* Related links */}
      {project.relatedLinks && project.relatedLinks.length > 0 && (
        <div style={{ marginTop: '48px', paddingTop: '24px', borderTop: '1px solid var(--border)' }}>
          <div className="eyebrow" style={{ marginBottom: '14px' }}>Related</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {project.relatedLinks.filter((link) => link && link.href).map((link) => (
              <Link
                key={link.href}
                to={link.href}
                style={{
                  fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
                  fontSize: '0.78rem',
                  color: 'var(--gold)',
                  textDecoration: 'none',
                  padding: '6px 12px',
                  border: '1px solid var(--gold-dim)',
                  borderRadius: '4px',
                  background: 'var(--gold-dim)',
                  transition: 'border-color 0.15s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--gold)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; }}
              >
                {link.label} →
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Bottom back link */}
      <div style={{ marginTop: '56px', paddingTop: '24px', borderTop: '1px solid var(--border)' }}>
        <Link
          to="/projects"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--text-3)',
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.65rem',
            letterSpacing: '0.06em',
            textDecoration: 'none',
            transition: 'color 0.15s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--gold)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-3)'; }}
        >
          ← PROJECTS
        </Link>
      </div>
    </div>
  );
}
